/**
 * Sprint 232 — Talent Creator Profile Editor
 *
 * Talent-facing page to manage the creator-marketplace fields used by
 * the Influencer Discovery V2 experience.
 *
 *  • Intro video (upload OR paste URL)
 *  • Creator categories (multi-select from canonical list)
 *  • Influencer tier (UGC, Micro, Mid, Macro, Mega)
 *  • Follower count / engagement rate / avg views
 *  • Manual "Worked With" brand entries
 *  • Media kit URL
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import SocialAccountsPanel from '../../components/SocialAccountsPanel';
import {
  ArrowLeft, Sparkles, Video, Link as LinkIcon, Upload, Plus, X,
  Loader2, Save, FileText, Trash2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const TalentCreatorProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [meta, setMeta] = useState({ tiers: [], categories: [] });

  const [form, setForm] = useState({
    intro_video_url: '',
    intro_video_upload_url: '',
    creator_categories: [],
    influencer_tier: '',
    follower_count: '',
    engagement_rate: '',
    average_views: '',
    media_kit_url: '',
    worked_with_brands: [],
    // Iter-247 — Creator Network MVP fields
    is_influencer: false,
    is_content_creator: false,
    // Iter-252 — Unified Creator Type field
    creator_type: '',
    bio: '',
    instagram_handle: '',
    tiktok_handle: '',
    youtube_channel: '',
    facebook_handle: '',
    facebook_followers: '',
    website_url: '',
    audience_locations: [],
    content_types: [],
  });
  const [brandDraft, setBrandDraft] = useState({ brand_name: '', brand_logo_url: '' });
  const [audienceDraft, setAudienceDraft] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, profRes] = await Promise.all([
        api.get('/influencer/meta'),
        api.get('/influencer/creator-profile'),
      ]);
      setMeta(metaRes.data || { tiers: [], categories: [], content_types: [] });
      const p = profRes.data || {};
      setForm({
        intro_video_url: p.intro_video_url || '',
        intro_video_upload_url: p.intro_video_upload_url || '',
        creator_categories: Array.isArray(p.creator_categories) ? p.creator_categories : [],
        influencer_tier: p.influencer_tier || '',
        follower_count: p.follower_count != null ? String(p.follower_count) : '',
        engagement_rate: p.engagement_rate != null ? String(p.engagement_rate) : '',
        average_views: p.average_views != null ? String(p.average_views) : '',
        media_kit_url: p.media_kit_url || '',
        worked_with_brands: (Array.isArray(p.worked_with_brands) ? p.worked_with_brands : [])
          .filter(b => b && (b.source || 'manual') === 'manual'),
        // Iter-247
        is_influencer: !!p.is_influencer,
        is_content_creator: !!p.is_content_creator,
        // Iter-252 — derive from creator_type if API returns it
        creator_type: p.creator_type || (
          p.is_influencer && p.is_content_creator ? 'both'
          : p.is_influencer ? 'influencer'
          : p.is_content_creator ? 'content_creator'
          : ''
        ),
        bio: p.bio || '',
        instagram_handle: p.instagram_handle || '',
        tiktok_handle: p.tiktok_handle || '',
        youtube_channel: p.youtube_channel || '',
        facebook_handle: p.facebook_handle || '',
        facebook_followers: p.facebook_followers != null ? String(p.facebook_followers) : '',
        website_url: p.website_url || '',
        audience_locations: Array.isArray(p.audience_locations) ? p.audience_locations : [],
        content_types: Array.isArray(p.content_types) ? p.content_types : [],
      });
    } catch (e) {
      toast.error('Failed to load creator profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      creator_categories: prev.creator_categories.includes(cat)
        ? prev.creator_categories.filter(c => c !== cat)
        : [...prev.creator_categories, cat],
    }));
  };

  // Iter-247 — Content Type multi-select
  const toggleContentType = (ct) => {
    setForm(prev => ({
      ...prev,
      content_types: prev.content_types.includes(ct)
        ? prev.content_types.filter(c => c !== ct)
        : [...prev.content_types, ct],
    }));
  };

  // Iter-247 — Audience location chip add/remove
  const addAudience = () => {
    const v = audienceDraft.trim();
    if (!v) return;
    if (form.audience_locations.some(x => x.toLowerCase() === v.toLowerCase())) {
      setAudienceDraft('');
      return;
    }
    setForm(prev => ({ ...prev, audience_locations: [...prev.audience_locations, v] }));
    setAudienceDraft('');
  };
  const removeAudience = (v) => {
    setForm(prev => ({
      ...prev,
      audience_locations: prev.audience_locations.filter(x => x !== v),
    }));
  };

  const addBrand = () => {
    const name = brandDraft.brand_name.trim();
    if (!name) return toast.error('Brand name is required');
    if (form.worked_with_brands.find(b => b.brand_name.toLowerCase() === name.toLowerCase())) {
      return toast.error('That brand is already in your list');
    }
    setForm(prev => ({
      ...prev,
      worked_with_brands: [
        ...prev.worked_with_brands,
        { brand_name: name, brand_logo_url: brandDraft.brand_logo_url.trim() || null },
      ],
    }));
    setBrandDraft({ brand_name: '', brand_logo_url: '' });
  };

  const removeBrand = (name) => {
    setForm(prev => ({
      ...prev,
      worked_with_brands: prev.worked_with_brands.filter(b => b.brand_name !== name),
    }));
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return toast.error('Video must be under 50MB');
    setUploadingVideo(true);
    try {
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/intro-videos',
        resource_type: 'video',
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('signature', signature);
      fd.append('timestamp', timestamp);
      fd.append('api_key', api_key);
      fd.append('folder', 'ieventur/intro-videos');
      const r = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/video/upload`, {
        method: 'POST',
        body: fd,
      });
      const data = await r.json();
      if (!data.secure_url) throw new Error('Upload failed');
      setForm(prev => ({ ...prev, intro_video_upload_url: data.secure_url }));
      toast.success('Intro video uploaded');
    } catch (e) {
      toast.error('Video upload failed');
    } finally {
      setUploadingVideo(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        intro_video_url: form.intro_video_url || null,
        intro_video_upload_url: form.intro_video_upload_url || null,
        creator_categories: form.creator_categories,
        worked_with_brands: form.worked_with_brands,
        media_kit_url: form.media_kit_url || null,
        // Iter-247 — Creator Network MVP
        is_influencer: !!form.is_influencer,
        is_content_creator: !!form.is_content_creator,
        // Iter-252 — Unified Creator Type (drives the two booleans server-side)
        creator_type: form.creator_type || (
          form.is_influencer && form.is_content_creator ? 'both'
          : form.is_influencer ? 'influencer'
          : form.is_content_creator ? 'content_creator'
          : null
        ),
        bio: form.bio || null,
        instagram_handle: form.instagram_handle || null,
        tiktok_handle: form.tiktok_handle || null,
        youtube_channel: form.youtube_channel || null,
        facebook_handle: form.facebook_handle || null,
        website_url: form.website_url || null,
        audience_locations: form.audience_locations,
        content_types: form.content_types,
      };
      if (form.influencer_tier) payload.influencer_tier = form.influencer_tier;
      if (form.follower_count !== '') payload.follower_count = parseInt(form.follower_count, 10) || 0;
      if (form.engagement_rate !== '') payload.engagement_rate = parseFloat(form.engagement_rate) || 0;
      if (form.average_views !== '') payload.average_views = parseInt(form.average_views, 10) || 0;
      if (form.facebook_followers !== '') payload.facebook_followers = parseInt(form.facebook_followers, 10) || 0;
      await api.put('/influencer/creator-profile', payload);
      toast.success('Creator profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#0DEAE3]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="talent-creator-profile">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Sparkles className="w-5 h-5 text-[#0DEAE3]" />
          <h1 className="text-lg font-semibold">Creator Profile</h1>
          <div className="flex-1" />
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-gray-900 font-semibold"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Iter-247 — Creator Network Identity */}
        <Card data-testid="creator-network-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0DEAE3]" />
              Creator Network
            </CardTitle>
            <p className="text-xs text-gray-500">
              Opt in to the iEvntür Creator Network. Both Influencers and Content Creators are
              discoverable by clients searching for creators.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            {/* Iter-252 — Single Creator Type selector. Selecting any option
                auto-flips the legacy is_influencer / is_content_creator booleans
                server-side, so the talent appears in Creator Network discovery. */}
            {[
              { id: 'influencer',      title: 'Influencer',           desc: 'Brand-focused presence with follower scale. Discovered by brands for audience reach and on-event promotion.' },
              { id: 'content_creator', title: 'Content Creator',      desc: 'UGC, photography, video, written content. Discovered by brands looking for content production rather than (or in addition to) audience reach.' },
              { id: 'both',            title: 'Both',                 desc: 'I create content AND have an audience. Shown to clients filtering for either type.' },
            ].map((opt) => {
              const active = form.creator_type === opt.id;
              return (
                <label
                  key={opt.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    active ? 'border-[#0DEAE3] bg-[#0DEAE3]/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`creator-type-option-${opt.id}`}
                >
                  <input
                    type="radio"
                    name="creator_type"
                    checked={active}
                    onChange={() =>
                      setForm({
                        ...form,
                        creator_type: opt.id,
                        is_influencer: opt.id === 'influencer' || opt.id === 'both',
                        is_content_creator: opt.id === 'content_creator' || opt.id === 'both',
                      })
                    }
                    className="mt-1 h-4 w-4 text-[#0DEAE3] focus:ring-[#0DEAE3]"
                    data-testid={`creator-type-radio-${opt.id}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900">{opt.title}</div>
                    <div className="text-xs text-gray-500">{opt.desc}</div>
                  </div>
                </label>
              );
            })}
            {form.creator_type && (
              <button
                type="button"
                onClick={() =>
                  setForm({ ...form, creator_type: '', is_influencer: false, is_content_creator: false })
                }
                className="text-xs text-gray-500 hover:text-gray-700 underline mt-1"
                data-testid="creator-type-clear"
              >
                Remove me from Creator Network
              </button>
            )}
          </CardContent>
        </Card>

        {/* Iter-247 — Creator Bio + Social Handles + Website */}
        <Card data-testid="creator-handles-card">
          <CardHeader>
            <CardTitle className="text-base">Creator Bio &amp; Handles</CardTitle>
            <p className="text-xs text-gray-500">Shown on your Creator Network profile.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Bio</Label>
              <textarea
                rows={3}
                className="mt-1.5 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Tell brands who you are, what you create, and who you reach."
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                data-testid="creator-bio-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Instagram handle</Label>
                <Input
                  placeholder="username"
                  value={form.instagram_handle}
                  onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })}
                  data-testid="instagram-handle-input"
                />
              </div>
              <div>
                <Label className="text-xs">TikTok handle</Label>
                <Input
                  placeholder="username"
                  value={form.tiktok_handle}
                  onChange={(e) => setForm({ ...form, tiktok_handle: e.target.value })}
                  data-testid="tiktok-handle-input"
                />
              </div>
              <div>
                <Label className="text-xs">YouTube channel</Label>
                <Input
                  placeholder="channel name or URL"
                  value={form.youtube_channel}
                  onChange={(e) => setForm({ ...form, youtube_channel: e.target.value })}
                  data-testid="youtube-channel-input"
                />
              </div>
              <div>
                <Label className="text-xs">Facebook handle</Label>
                <Input
                  placeholder="page or profile name"
                  value={form.facebook_handle}
                  onChange={(e) => setForm({ ...form, facebook_handle: e.target.value })}
                  data-testid="facebook-handle-input"
                />
              </div>
              <div>
                <Label className="text-xs">Facebook followers</Label>
                <Input
                  type="number"
                  value={form.facebook_followers}
                  onChange={(e) => setForm({ ...form, facebook_followers: e.target.value })}
                  data-testid="facebook-followers-input"
                />
              </div>
              <div>
                <Label className="text-xs">Website</Label>
                <Input
                  placeholder="https://yourdomain.com"
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  data-testid="website-url-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Iter-247 — Audience Location + Content Types */}
        <Card data-testid="creator-audience-card">
          <CardHeader>
            <CardTitle className="text-base">Audience &amp; Content</CardTitle>
            <p className="text-xs text-gray-500">Where is your audience and what do you create?</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Audience locations</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  placeholder="e.g. Atlanta, GA"
                  value={audienceDraft}
                  onChange={(e) => setAudienceDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAudience(); } }}
                  data-testid="audience-input"
                />
                <Button
                  type="button"
                  onClick={addAudience}
                  className="bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-gray-900"
                  data-testid="audience-add-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.audience_locations.length === 0 ? (
                <p className="mt-2 text-xs text-gray-400">No audience locations added yet.</p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="audience-chips">
                  {form.audience_locations.map(loc => (
                    <Badge key={loc} className="bg-[#0DEAE3]/15 text-[#055f5d] border border-[#0DEAE3]/40 pl-2 pr-1">
                      {loc}
                      <button
                        type="button"
                        onClick={() => removeAudience(loc)}
                        className="ml-1 inline-flex w-4 h-4 rounded-full hover:bg-red-100 text-red-600 items-center justify-center"
                        data-testid={`audience-remove-${loc.replace(/\s|,/g, '-').toLowerCase()}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Content types</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(meta.content_types || []).map(ct => {
                  const on = form.content_types.includes(ct);
                  return (
                    <button
                      key={ct}
                      type="button"
                      onClick={() => toggleContentType(ct)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        on
                          ? 'bg-[#0DEAE3] border-[#0DEAE3] text-gray-900'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-[#0DEAE3]'
                      }`}
                      data-testid={`content-type-${ct.replace(/\s|\//g, '-').toLowerCase()}`}
                    >
                      {ct}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intro Video */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Video className="w-4 h-4 text-[#0DEAE3]" /> Intro Video
            </CardTitle>
            <p className="text-xs text-gray-500">30 sec – 2 min. Upload directly, or paste a YouTube / Vimeo / social link.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Upload Video (max 50MB)</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => uploadVideo(e.target.files?.[0])}
                    data-testid="upload-video-input"
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-sm border border-gray-300">
                    {uploadingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingVideo ? 'Uploading…' : 'Choose video'}
                  </span>
                </label>
                {form.intro_video_upload_url && (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs text-emerald-700 truncate max-w-xs">Uploaded</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm(prev => ({ ...prev, intro_video_upload_url: '' }))}
                      className="text-red-600 hover:text-red-700"
                      data-testid="remove-uploaded-video"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
              {form.intro_video_upload_url && (
                <video src={form.intro_video_upload_url} controls className="mt-3 w-full max-w-md rounded-lg bg-black" />
              )}
            </div>

            <div>
              <Label className="text-xs">Or paste a video URL</Label>
              <div className="mt-1.5 relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="https://youtube.com/watch?v=…"
                  value={form.intro_video_url}
                  onChange={(e) => setForm({ ...form, intro_video_url: e.target.value })}
                  data-testid="video-url-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tier & Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tier &amp; Audience Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sprint 233.A — Social Accounts (Instagram / TikTok / YouTube) */}
            <SocialAccountsPanel />
            <div className="border-t border-slate-200 pt-4" />
            <div>
              <Label className="text-xs">Influencer Tier</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {meta.tiers.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setForm({ ...form, influencer_tier: t.id })}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      form.influencer_tier === t.id
                        ? 'bg-[#0DEAE3] border-[#0DEAE3] text-gray-900'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-[#0DEAE3]'
                    }`}
                    data-testid={`tier-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Total Followers</Label>
                <Input
                  type="number"
                  value={form.follower_count}
                  onChange={(e) => setForm({ ...form, follower_count: e.target.value })}
                  data-testid="follower-count"
                />
              </div>
              <div>
                <Label className="text-xs">Engagement Rate %</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={form.engagement_rate}
                  onChange={(e) => setForm({ ...form, engagement_rate: e.target.value })}
                  data-testid="engagement-rate"
                />
              </div>
              <div>
                <Label className="text-xs">Average Views</Label>
                <Input
                  type="number"
                  value={form.average_views}
                  onChange={(e) => setForm({ ...form, average_views: e.target.value })}
                  data-testid="average-views"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Creator Categories</CardTitle>
            <p className="text-xs text-gray-500">Pick all that describe your content.</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {meta.categories.map(c => {
                const on = form.creator_categories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      on
                        ? 'bg-[#0DEAE3] border-[#0DEAE3] text-gray-900'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-[#0DEAE3]'
                    }`}
                    data-testid={`category-${c.replace(/\s/g, '-').toLowerCase()}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Worked With */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Worked With Brands</CardTitle>
            <p className="text-xs text-gray-500">Past completed clients show automatically. Add other brand collaborations here.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                placeholder="Brand name (e.g. Nike)"
                value={brandDraft.brand_name}
                onChange={(e) => setBrandDraft({ ...brandDraft, brand_name: e.target.value })}
                data-testid="brand-name-input"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="Logo URL (optional)"
                  value={brandDraft.brand_logo_url}
                  onChange={(e) => setBrandDraft({ ...brandDraft, brand_logo_url: e.target.value })}
                  data-testid="brand-logo-input"
                />
                <Button onClick={addBrand} className="bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-gray-900" data-testid="add-brand-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {form.worked_with_brands.length === 0 ? (
              <p className="text-xs text-gray-400">No manual brand entries yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="brand-chips">
                {form.worked_with_brands.map(b => (
                  <Badge key={b.brand_name} className="bg-[#0DEAE3]/15 text-[#055f5d] border border-[#0DEAE3]/40 hover:bg-[#0DEAE3]/20 pr-1 pl-2">
                    {b.brand_logo_url && (
                      <img src={b.brand_logo_url} alt="" className="w-3 h-3 rounded-sm object-cover mr-1.5 inline-block align-middle" />
                    )}
                    {b.brand_name}
                    <button
                      type="button"
                      onClick={() => removeBrand(b.brand_name)}
                      className="ml-1 inline-flex w-4 h-4 rounded-full hover:bg-red-100 text-red-600 items-center justify-center"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Media Kit */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#0DEAE3]" /> Media Kit
            </CardTitle>
            <p className="text-xs text-gray-500">Optional link to your full media kit (Notion, Google Doc, PDF, …).</p>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="https://…"
              value={form.media_kit_url}
              onChange={(e) => setForm({ ...form, media_kit_url: e.target.value })}
              data-testid="media-kit-url"
            />
          </CardContent>
        </Card>

        <div className="pt-2 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#0DEAE3] hover:bg-[#0DEAE3]/90 text-gray-900 font-semibold"
            data-testid="save-btn-bottom"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save Creator Profile
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TalentCreatorProfile;
